import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { Avatar, Form, Input, Modal, message } from "antd";
import { SpellCheckTextArea } from "../spellcheck";
import Button from "antd-button-color";
import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { cloudFrontUrl, serverUrl } from "../../nestserver";
import { selectUser } from "../../redux/slices/auth.slice";
import MediaCarousel from "../media/MediaCarousel";

function FeedSection({ data, enableDelete = false }) {
  const user = useSelector(selectUser);
  const queryClient = useQueryClient();
  const { confirm } = Modal;
  const [edit, setEdit] = useState(false);
  const [media, setMedia] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage] = useState("");
  const [form] = Form.useForm();

  // A post carrying `group` was published by the school group and shows in every
  // branch's feed. Only a group admin of that same group may change it — a
  // branch admin seeing it here would otherwise get a 403, or worse, delete the
  // group's post for every branch. The backend enforces this regardless.
  const canModify =
    !data?.group || String(user?.group ?? "") === String(data.group);

  const confirmDelete = () => {
    confirm({
      title: "Do you want to delete?",
      icon: <ExclamationCircleOutlined />,
      okText: "Yes",
      cancelText: "No",
      onOk() {
        deleteAnnouncement();
      },
      onCancel() {
        console.log("Cancel");
      },
    });
  };

  const deleteAnnouncement = () => {
    axios({
      method: "delete",
      url: serverUrl + "/announcement/" + data?._id,
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    })
      .then((res) => {
        message.success("Deleted");
        queryClient.invalidateQueries("announcement");
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const onAnnounce = (values) => {
    const imageData = media
      .filter((item) => item.type === "IMAGE")
      .map((img, index) => ({
        id: `img_${Date.now()}_${index}`,
        name: img.name || `image_${index}.jpg`,
        url: img.url || img.image,
      }));

    const mediaData = media.map((item, index) => ({
      id: item.id || `media_${Date.now()}_${index}`,
      name: item.name || `media_${index}`,
      type: item.type,
      url: item.url || item.image || item.file,
    }));

    values["images"] = imageData;
    values["media"] = mediaData;

    axios({
      method: "patch",
      url: serverUrl + "/announcement/" + data?._id,
      data: { ...values, content: values?.content?.trim() },
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    })
      .then((res) => {
        message.success("Announcement Updated");
        form.resetFields();
        queryClient.invalidateQueries("announcement");
        setMedia([]);
        setEdit(false);
      })
      .catch((e) => {
        message.error(e.message);
      });
  };

  const removeMediaInEdit = (indexToRemove) => {
    setMedia((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const getImageDisplayUrl = (image) => {
    if (!image) return "";
    if (typeof image === "string") {
      return cloudFrontUrl + "/" + data?.user?._id + "/" + image;
    }
    if (image.url) {
      return cloudFrontUrl + "/" + data?.user?._id + "/" + image.url;
    }
    if (image.image) {
      return cloudFrontUrl + "/" + data?.user?._id + "/" + image.image;
    }
    return "";
  };

  useEffect(() => {
    if (edit) {
      // Initialize from media if available, otherwise convert images to media format
      if (data?.media && data.media.length > 0) {
        setMedia(data.media);
      } else if (data?.images && data.images.length > 0) {
        // Legacy support: convert images to media format
        setMedia(
          data.images.map((img) => ({
            ...img,
            type: "IMAGE",
            url: img.url || img,
          })),
        );
      } else {
        setMedia([]);
      }
      form?.resetFields();
    }
  }, [data, edit, form]);

  const getUserProfile = (user) => {
    if (!user) return null;
    if (user.first_name || user.last_name || user.profile_image) return user;

    return user.admin || user.teacher || user.instructor || user;
  };

  const getUserRole = (user) => {
    if (!user) return null;
    if (user.admin) return "Admin";
    if (user.teacher || user.instructor) return "Instructor";
    return null;
  };

  const userProfile = getUserProfile(data?.user);
  const userRole = getUserRole(data?.user);

  return (
    <>
      <div className="bg-white rounded-xl border border-gray-200">
        {data && (
          <div className="py-5 px-5">
            <div className="flex justify-between">
              <div
                style={{ color: "#616161" }}
                className="flex flex-row space-x-3 text-left text-lg px-5"
              >
                <div>
                  <Avatar
                    size={50}
                    src={
                      userProfile?.profile_image
                        ? cloudFrontUrl +
                          "/" +
                          data?.user?._id +
                          "/" +
                          userProfile.profile_image
                        : "https://ui-avatars.com/api/?name=" +
                          (userProfile?.first_name || "Unknown") +
                          " " +
                          (userProfile?.last_name || "")
                    }
                    alt="logo"
                  ></Avatar>
                </div>
                <div className="flex flex-col">
                  <div
                    style={{ color: "#09617E" }}
                    className="text-xl flex items-center gap-3"
                  >
                    <span>
                      {`${userProfile?.first_name || "Unknown"} ${
                        userProfile?.last_name || ""
                      }`}
                    </span>
                    {userRole && (
                      <span
                        style={{
                          backgroundColor:
                            userRole === "Admin"
                              ? "rgba(99, 102, 241, 0.1)"
                              : "rgba(20, 184, 166, 0.1)",
                          border: `1px solid ${
                            userRole === "Admin"
                              ? "rgba(99, 102, 241, 0.4)"
                              : "rgba(20, 184, 166, 0.4)"
                          }`,
                          color: userRole === "Admin" ? "#4f46e5" : "#0d9488",
                        }}
                        className="text-xs font-semibold px-3 py-1 rounded-full"
                      >
                        {userRole}
                      </span>
                    )}
                  </div>
                  <div>
                    {moment(data?.createdAt).format("MMM Do YYYY, h:mm a")}
                  </div>
                </div>
              </div>
              <div className="inline-flex items-center space-x-3">
                {data?.group && (
                  <span
                    className="text-xs uppercase tracking-wide rounded px-2 py-1"
                    style={{ background: "#E7F0F5", color: "#1B5E7E" }}
                    title="Posted by your school group — visible at every branch"
                  >
                    All branches
                  </span>
                )}
                {enableDelete && canModify && (
                  <div className="inline-flex space-x-3 text-lg">
                    <EditOutlined
                      onClick={() => setEdit(true)}
                      className=" text-gray-400 hover:text-yellow-500 cursor-pointer"
                    />
                    <DeleteOutlined
                      className=" text-gray-400 hover:text-red-500 cursor-pointer"
                      onClick={confirmDelete}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="text-lg text-left px-5 pt-4">
              <p
                style={{
                  wordWrap: "break-word",
                  whiteSpace: "normal",
                }}
                className="mb-3"
              >
                {data?.content}
              </p>

              <div className="space-y-4">
                {/* 1. Visual Media Carousel (Images & Videos) */}
                {(data?.media?.filter(
                  (item) => item.type === "IMAGE" || item.type === "VIDEO",
                ).length > 0 ||
                  data?.images?.length > 0) && (
                  <div className="mb-4">
                    <MediaCarousel
                      mediaItems={
                        data?.media && data.media.length > 0
                          ? data.media.filter(
                              (item) =>
                                item.type === "IMAGE" || item.type === "VIDEO",
                            )
                          : (data?.images || []).map((img) => ({
                              ...img,
                              type: "IMAGE",
                              url: img.url || img,
                            }))
                      }
                      getImageUrl={getImageDisplayUrl}
                    />
                  </div>
                )}

                {/* 2. Audio Files (List) */}
                {data?.media
                  ?.filter((item) => item.type === "AUDIO")
                  .map((item, index) => (
                    <div key={`audio-${index}`} className="max-w-md">
                      <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-xl border border-purple-200 shadow-sm">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-2xl">🎵</span>
                          <span className="text-sm font-medium text-gray-700">
                            Audio File
                          </span>
                        </div>
                        <audio controls className="w-full">
                          <source
                            src={getImageDisplayUrl(item)}
                            type="audio/mpeg"
                          />
                          Your browser does not support the audio element.
                        </audio>
                      </div>
                    </div>
                  ))}

                {/* 3. Voice Recordings (List) */}
                {data?.media
                  ?.filter((item) => item.type === "VOICE")
                  .map((item, index) => (
                    <div key={`voice-${index}`} className="max-w-md">
                      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-xl border border-blue-200 shadow-sm">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-2xl">🎙️</span>
                          <span className="text-sm font-medium text-gray-700">
                            Voice Message
                          </span>
                        </div>
                        <audio controls className="w-full">
                          <source
                            src={getImageDisplayUrl(item)}
                            type="audio/mpeg"
                          />
                          <source
                            src={getImageDisplayUrl(item)}
                            type="audio/wav"
                          />
                          Your browser does not support the audio element.
                        </audio>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        <Modal
          visible={previewVisible}
          footer={null}
          onCancel={() => setPreviewVisible(false)}
          width="auto"
          bodyStyle={{ padding: 0 }}
          centered
        >
          <div className="flex justify-center items-center">
            <img
              alt="preview"
              src={previewImage}
              style={{
                maxWidth: "80vw",
                maxHeight: "70vh",
                objectFit: "contain",
              }}
            />
          </div>
        </Modal>

        <Modal
          title="Edit Announcement"
          visible={edit}
          onCancel={() => setEdit(false)}
          okText="Save"
          onOk={() => form.submit()}
          afterClose={() => {
            form.resetFields();
            setMedia([]);
          }}
          width={800}
        >
          <div className="mb-3">
            {media.length > 0 && (
              <div className="mb-6">
                <div className="text-sm font-medium text-gray-700 mb-2">
                  Selected Media ({media.length}/10)
                </div>
                <div className="flex flex-wrap gap-3">
                  {media.map((item, index) => (
                    <div key={index} className="relative w-20 h-20">
                      <div className="border-2 border-gray-200 rounded-lg bg-gray-100 shadow-sm w-full h-full flex items-center justify-center overflow-hidden hover:border-blue-400 transition-colors">
                        {item.type === "IMAGE" && (
                          <img
                            src={getImageDisplayUrl(item)}
                            alt={`Upload ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        )}
                        {item.type === "VIDEO" && (
                          <div className="relative w-full h-full bg-gray-900">
                            <video
                              src={`${getImageDisplayUrl(item)}#t=0.1`}
                              className="w-full h-full object-cover"
                              preload="metadata"
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                              <div className="text-white text-3xl">▶️</div>
                            </div>
                          </div>
                        )}
                        {item.type === "AUDIO" && (
                          <div className="relative w-full h-full bg-gradient-to-br from-pink-100 to-orange-100 flex items-center justify-center">
                            <div className="text-3xl">🎵</div>
                          </div>
                        )}
                        {item.type === "VOICE" && (
                          <div className="relative w-full h-full bg-gradient-to-br from-blue-100 to-cyan-100 flex items-center justify-center">
                            <div className="text-3xl">🎙️</div>
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeMediaInEdit(index)}
                        className="absolute top-0 right-0 rounded-full flex items-center justify-center font-bold shadow-lg hover:scale-110 transition-transform z-10"
                        style={{
                          width: "24px",
                          height: "24px",
                          backgroundColor: "#ef4444",
                          color: "white",
                          fontSize: "16px",
                          cursor: "pointer",
                        }}
                        title="Remove media"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* <ImageUploadButton
              folder={data?.user?._id}
              ratio={1}
              onUpload={handleImageUpload}
              multiple={true}
              disabled={images.length >= 4}
            >
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-blue-500 transition-colors">
                <div className="text-gray-500">
                  <div className="text-lg">
                    Upload Images ({images.length}/4)
                  </div>
                  <div className="text-sm">Click to add images (Max 4)</div>
                </div>
              </div>
            </ImageUploadButton> */}

            {media.length > 0 && (
              <div className="mt-4 py-4">
                <Button type="danger" onClick={() => setMedia([])} size="small">
                  Remove All Media
                </Button>
              </div>
            )}
          </div>

          <Form
            name="basic"
            initialValues={{ content: data?.content }}
            onFinish={onAnnounce}
            form={form}
          >
            <Form.Item
              name="content"
              rules={[{ required: true, message: "Please input the content" }]}
            >
              <SpellCheckTextArea
                rows={3}
                placeholder="Description"
                className="w-full border focus:outline-none resize-none"
              />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </>
  );
}

export default FeedSection;
